import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Mail, Clock, MessageCircle, CheckCircle } from 'lucide-react';

const EnquiryDetailModal = ({ 
  isOpen, 
  onClose, 
  enquiry, 
  onStatusUpdate, 
  onNavigate 
}) => {
  if (!enquiry) return null;

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'pending': 'badge-warning',
      'responded': 'badge-success'
    };
    return statusMap[status] || 'badge-neutral';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4 p-0" data-testid="enquiry-detail-modal">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-purple-500/10 to-purple-300/10 p-6 rounded-t-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              <div className="icon-container-purple">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              Enquiry Details
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-6">
          {/* Enquirer Info Card */}
          <div className="card-premium p-5">
            <div className="flex items-start gap-4">
              <Avatar className="w-16 h-16 ring-2 ring-primary/20 ring-offset-2 ring-offset-background">
                <AvatarImage src={enquiry.enquirer?.profileImage} alt={enquiry.enquirer?.name} />
                <AvatarFallback className="text-lg bg-primary/10 text-primary font-semibold">
                  {enquiry.enquirer?.name?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-bold text-foreground">{enquiry.enquirer?.name || 'Unknown User'}</h3>
                    <p className="text-sm text-muted-foreground">@{enquiry.enquirer?.username || 'unknown'}</p>
                  </div>
                  <span className={getStatusBadgeClass(enquiry.status)}>
                    {enquiry.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant="outline" className="text-xs">{enquiry.enquirer?.persona || 'User'}</Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Subject</label>
            <p className="text-base font-medium text-foreground">{enquiry.subject}</p>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Message</label>
            <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{enquiry.message}</p>
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {enquiry.phone && (
              <div className="space-y-2">
                <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Phone</label>
                <p className="text-sm text-foreground">{enquiry.phone}</p>
              </div>
            )}
            {(enquiry.email || enquiry.enquirer?.email) && (
              <div className="space-y-2">
                <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Email</label>
                <p className="text-sm text-foreground break-all">{enquiry.email || enquiry.enquirer.email}</p>
              </div>
            )}
          </div>

          {/* Preferred Contact */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Preferred Contact</label>
            <p className="text-sm text-foreground capitalize">{enquiry.preferredContact || 'Email'}</p>
          </div>

          {/* Timestamp */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>Received: {new Date(enquiry.createdAt).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</span>
          </div>

          {/* Action Buttons */}
          <div className="pt-4 border-t border-border">
            <label className="text-sm font-semibold mb-3 block">Update Status</label>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={enquiry.status === 'pending' ? 'default' : 'outline'}
                onClick={() => onStatusUpdate(enquiry._id, 'pending')}
                disabled={enquiry.status === 'pending'}
              >
                <Clock className="w-3.5 h-3.5 mr-1.5" />
                Pending
              </Button>
              <Button
                size="sm"
                variant={enquiry.status === 'responded' ? 'default' : 'outline'}
                onClick={() => onStatusUpdate(enquiry._id, 'responded')}
                disabled={enquiry.status === 'responded'}
                className={enquiry.status === 'responded' ? 'bg-primary hover:bg-purple-600' : ''}
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                Responded
              </Button>
              <Button
                size="sm"
                className="btn-primary"
                onClick={() => {
                  if (enquiry.enquirer?._id || enquiry.enquirerId) {
                    onNavigate(`/messages?userId=${enquiry.enquirer?._id || enquiry.enquirerId}`);
                    onClose(false);
                  }
                }}
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
                Send Message
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="p-4 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={() => onClose(false)} className="w-full sm:w-auto">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryDetailModal;
